// apps/lk/src/components/dashboard/BigFiveBlock.tsx
'use client';
import RadarChart from './RadarChart';
import { scoreColor, scoreBg } from './polarity';

const DOMAIN_DESC: Record<string, string> = {
  N: 'Уровень эмоциональной реактивности — тревожность, чувствительность к стрессу',
  E: 'Общительность, энергичность, поиск социальных контактов',
  O: 'Открытость новому опыту, идеям, креативность',
  A: 'Доброжелательность, готовность к сотрудничеству, эмпатия',
  C: 'Организованность, дисциплина, надёжность',
};

const DERIVED_DESC: Record<string, string> = {
  leadership: 'Склонность брать ответственность, вести за собой',
  stressResistance: 'Устойчивость к давлению и неопределённости',
  creativity: 'Способность генерировать новые идеи',
  discipline: 'Последовательность в достижении целей',
  socialSkills: 'Умение выстраивать отношения',
  empathy: 'Способность понимать чувства других',
  businessReliability: 'Надёжность в рабочих обязательствах',
};

const DERIVED_LABELS: Record<string, string> = {
  leadership: 'Лидерство',
  stressResistance: 'Стрессоустойчивость',
  creativity: 'Креативность',
  discipline: 'Дисциплина',
  socialSkills: 'Социальные навыки',
  empathy: 'Эмпатия',
  businessReliability: 'Деловая надёжность',
};

export function BigFiveBlock({ data }: { data: any }) {
  const d = data;
  if (!d?.domains) return null;

  const radarPoints = (['N', 'E', 'O', 'A', 'C'] as const).map(k => ({
    label: d.domains[k].name,
    value: d.domains[k].value,
  }));

  // Сильные стороны — top-3 derived
  const derived = d.derived ?? {};
  const derivedSorted = Object.entries(derived)
    .filter(([, v]) => v != null)
    .sort(([, a], [, b]) => (b as number) - (a as number));

  return (
    <section className="mb-8">
      <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Личность</h2>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Радар 5 доменов */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">5 базовых черт</h3>
          {radarPoints.length >= 3 ? (
            <RadarChart points={radarPoints} size={260} />
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">Недостаточно данных</div>
          )}
        </div>

        {/* Описание доменов */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Что это значит</h3>
          <div className="space-y-3">
            {(['N', 'E', 'O', 'A', 'C'] as const).map(k => {
              const dom = d.domains[k];
              return (
                <div key={k}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800">{dom.name}</span>
                    <span className={`text-sm font-bold ${scoreColor(dom.value)}`}>{dom.value}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full ${scoreBg(dom.value)}`} style={{ width: `${dom.value}%` }} />
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{DOMAIN_DESC[k]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Производные метрики */}
      {derivedSorted.length > 0 && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-slate-700">Производные качества</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {derivedSorted.map(([key, val]) => (
              <DerivedCard key={key} label={DERIVED_LABELS[key] ?? key} value={val as number} desc={DERIVED_DESC[key] ?? ''} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function DerivedCard({ label, value, desc }: { label: string; value: number; desc: string }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-800">{label}</span>
        <span className={`text-sm font-bold ${scoreColor(value)}`}>{value}</span>
      </div>
      <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full ${scoreBg(value)}`} style={{ width: `${value}%` }} />
      </div>
      <p className="text-xs leading-relaxed text-slate-500">{desc}</p>
    </div>
  );
}
